<template>
  <div>
    <b-card
      title="Ajouter un manager"
      aria-label="Formulaire d'ajout d'un manager"
    >
      <b-form
        ref="managerForm"
        aria-label="Formulaire d'ajout d'un manager"
        @submit.prevent="saveManager"
      >
        <!-- Informations personnelles -->
        <h5 class="mb-2">
          <feather-icon
            icon="UserIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Informations personnelles du manager
        </h5>

        <b-row>
          <b-col md="6">
            <b-form-group
              label="Nom complet du manager *"
              label-for="username"
              description="Saisissez le nom complet tel qu'il figure sur les documents officiels. Champ obligatoire."
            >
              <b-form-input
                id="username"
                v-model="form.username"
                :state="nameState"
                placeholder="Entrez le nom complet du manager"
                aria-label="Nom complet du manager"
                aria-required="true"
                aria-describedby="username-desc"
              />
              <b-form-invalid-feedback
                v-if="submitted && !form.username"
                aria-live="polite"
              >
                Le nom complet est requis
              </b-form-invalid-feedback>
              <small
                id="username-desc"
                class="sr-only"
              >Ce champ est requis pour l'identification du manager dans l'organisation logistique.</small>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
              label="Adresse e-mail professionnelle *"
              label-for="email"
              description="Saisissez l'adresse e-mail professionnelle du manager. Champ obligatoire."
            >
              <b-form-input
                id="email"
                v-model="form.email"
                type="email"
                :state="emailState"
                placeholder="Entrez l'adresse e-mail professionnelle"
                aria-label="Adresse e-mail professionnelle"
                aria-required="true"
                aria-describedby="email-desc"
              />
              <b-form-invalid-feedback
                v-if="submitted && !form.email"
                aria-live="polite"
              >
                L'adresse e-mail est requise
              </b-form-invalid-feedback>
              <b-form-invalid-feedback
                v-if="submitted && form.email && !emailState"
                aria-live="polite"
              >
                Veuillez entrer une adresse e-mail valide
              </b-form-invalid-feedback>
              <small
                id="email-desc"
                class="sr-only"
              >Ce champ est requis pour la communication officielle avec le manager.</small>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>

          <b-col md="6">
            <b-form-group
              label="Téléphone professionnel *"
              label-for="phone"
              description="Saisissez le numéro de téléphone professionnel du manager (8 chiffres, uniquement des chiffres). Champ obligatoire."
            >
              <b-form-input
                id="phone"
                v-model="form.phone"
                placeholder="Entrez le numéro de téléphone professionnel"
                type="tel"
                maxlength="8"
                :formatter="formatPhone"
                :state="phoneState"
                aria-label="Numéro de téléphone professionnel"
                aria-required="true"
                aria-describedby="phone-desc"
                @keypress="allowOnlyNumbers"
              />
              <b-form-invalid-feedback
                v-if="form.phone && !phoneState"
                aria-live="polite"
              >
                Le numéro de téléphone doit contenir 8 chiffres
              </b-form-invalid-feedback>
              <b-form-text>Format: 8 chiffres, sans espaces</b-form-text>
              <small
                id="phone-desc"
                class="sr-only"
              >Ce champ est requis pour contacter le manager en cas d'urgence organisationnelle.</small>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
              label="Téléphone secondaire"
              label-for="phone2"
            >
              <b-form-input
                id="phone2"
                v-model="form.phone2"
                placeholder="Entrez le numéro de téléphone secondaire (optionnel)"
                type="tel"
                maxlength="8"
                :formatter="formatPhone"
                @keypress="allowOnlyNumbers"
              />
              <b-form-text>Format: 8 chiffres, sans espaces</b-form-text>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group
          label="Adresse"
          label-for="address"
        >
          <b-form-textarea
            id="address"
            v-model="form.address"
            placeholder="Entrez l'adresse complète"
            rows="3"
          />
        </b-form-group>

        <b-form-group
          label="CIN"
          label-for="cin"
        >
          <b-form-input
            id="cin"
            v-model="form.cin"
            placeholder="Entrez le numéro de CIN (optionnel)"
            maxlength="8"
            :formatter="formatCIN"
            :state="cinState"
            @keypress="allowOnlyNumbers"
          />
          <b-form-invalid-feedback v-if="form.cin && !cinState">
            Le numéro de CIN doit contenir 8 chiffres
          </b-form-invalid-feedback>
          <b-form-text>Format: 8 chiffres, sans espaces</b-form-text>
        </b-form-group>

        <!-- Repository Assignment -->
        <h5 class="mb-2 mt-4">
          <feather-icon
            icon="HomeIcon"
            class="mr-1"
          />
          Affectation au dépôt
        </h5>

        <b-form-group
          label="Dépôt *"
          label-for="repository"
          :invalid-feedback="submitted && !form.repository ? 'Le dépôt est requis' : ''"
          :state="repositoryState"
        >
          <v-select
            id="repository"
            v-model="form.repository"
            :options="repositories"
            label="name"
            :reduce="repo => repo.id"
            placeholder="Sélectionnez un dépôt"
            :class="[
              'enhanced-select',
              submitted && !form.repository ? 'is-invalid' : '',
              form.repository ? 'is-valid' : ''
            ]"
            :clearable="true"
            :searchable="true"
            :loading="repositoriesLoading"
            :disabled="repositoriesLoading"
            @input="onRepositoryChange"
          >
            <template #no-options>
              <span v-if="repositoriesLoading">Chargement des dépôts...</span>
              <span v-else>Aucun dépôt disponible</span>
            </template>
            <template #selected-option="{ name }">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="HomeIcon"
                  size="16"
                  class="mr-2"
                  style="color: #E31E24;"
                />
                <span>{{ name }}</span>
              </div>
            </template>
            <template #option="{ name }">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="HomeIcon"
                  size="16"
                  class="mr-2"
                  style="color: #E31E24;"
                />
                <span>{{ name }}</span>
              </div>
            </template>
            <template #spinner="{ loading }">
              <div
                v-if="loading"
                class="vs__spinner"
              >
                <div class="loading-dots">
                  <div class="dot1" />
                  <div class="dot2" />
                  <div class="dot3" />
                </div>
              </div>
            </template>
          </v-select>
        </b-form-group>

        <!-- Access Information -->
        <h5 class="mb-2 mt-4">
          <feather-icon
            icon="LockIcon"
            class="mr-1"
          />
          Informations de connexion
        </h5>

        <b-row>
          <b-col md="6">
            <b-form-group
              label="Mot de passe *"
              label-for="password"
            >
              <b-input-group class="input-group-merge">
                <b-form-input
                  id="password"
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  :state="passwordState"
                  placeholder="Entrez un mot de passe"
                />
                <b-input-group-append
                  is-text
                  @click="showPassword = !showPassword"
                >
                  <feather-icon
                    :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                    class="cursor-pointer"
                  />
                </b-input-group-append>
              </b-input-group>
              <b-form-invalid-feedback v-if="submitted && !form.password">
                Le mot de passe est requis
              </b-form-invalid-feedback>
              <b-form-invalid-feedback v-if="submitted && form.password && !passwordState">
                Le mot de passe doit comporter au moins 8 caractères
              </b-form-invalid-feedback>
              <b-form-text>Le mot de passe doit comporter au moins 8 caractères</b-form-text>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
              label="Confirmer le mot de passe *"
              label-for="confirm-password"
            >
              <b-input-group class="input-group-merge">
                <b-form-input
                  id="confirm-password"
                  v-model="form.confirm_password"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  :state="confirmPasswordState"
                  placeholder="Confirmez le mot de passe"
                />
                <b-input-group-append
                  is-text
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <feather-icon
                    :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                    class="cursor-pointer"
                  />
                </b-input-group-append>
              </b-input-group>
              <b-form-invalid-feedback v-if="submitted && !form.confirm_password">
                La confirmation du mot de passe est requise
              </b-form-invalid-feedback>
              <b-form-invalid-feedback v-if="submitted && form.confirm_password && !confirmPasswordState">
                Les mots de passe ne correspondent pas
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Submit buttons -->
        <div class="d-flex justify-content-between mt-4">
          <b-button
            variant="outline-secondary"
            :to="{ name: 'manager-list' }"
          >
            <feather-icon
              icon="XIcon"
              size="16"
              class="mr-1"
            />
            Annuler
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            :disabled="isSubmitting"
          >
            <b-spinner
              v-if="isSubmitting"
              small
              class="mr-1"
            />
            <feather-icon
              v-else
              icon="SaveIcon"
              size="16"
              class="mr-1"
            />
            Enregistrer
          </b-button>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BRow,
  BCol,
  BSpinner,
  BFormInvalidFeedback,
  BInputGroup,
  BInputGroupAppend,
  BFormText,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ManagerCreate',

  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BRow,
    BCol,
    BSpinner,
    BFormInvalidFeedback,
    BInputGroup,
    BInputGroupAppend,
    BFormText,
    vSelect,
  },

  data() {
    return {
      isSubmitting: false,
      submitted: false,
      repositories: [],
      repositoriesLoading: true,
      form: {
        username: '',
        email: '',
        phone: '',
        phone2: '',
        address: '',
        cin: '',
        password: '',
        confirm_password: '',
        repository: null,
      },
      showPassword: false,
      showConfirmPassword: false,
    }
  },

  computed: {
    nameState() {
      return this.form.username ? true : null
    },

    emailState() {
      if (!this.form.email) return null
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(this.form.email)
    },

    passwordState() {
      if (!this.form.password) return null
      return this.form.password.length >= 8
    },

    confirmPasswordState() {
      if (!this.form.confirm_password) return null
      return this.form.confirm_password === this.form.password
    },

    phoneState() {
      if (!this.form.phone) return true
      return this.form.phone.length === 8 && /^\d+$/.test(this.form.phone)
    },

    repositoryState() {
      return this.form.repository ? true : null
    },

    cinState() {
      if (!this.form.cin) return true // Optional field
      return this.form.cin.length === 8 && /^\d+$/.test(this.form.cin)
    },
  },

  created() {
    this.fetchRepositories()
  },

  mounted() {
    // Ensure proper CSS loading after component mount
    this.$nextTick(() => {
      this.refreshSelectStyles()
    })
  },

  // Handle route changes for proper CSS reloading
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.$nextTick(() => {
        vm.refreshSelectStyles()
      })
    })
  },

  activated() {
    // For keep-alive components
    this.$nextTick(() => {
      this.refreshSelectStyles()
    })
  },

  methods: {
    refreshSelectStyles() {
      // Force refresh of v-select styles
      const selectElements = this.$el.querySelectorAll('.enhanced-select')
      selectElements.forEach(element => {
        // Trigger a reflow to ensure styles are applied
        const originalDisplay = element.style.display
        // eslint-disable-next-line no-param-reassign
        element.style.display = 'none'
        // eslint-disable-next-line no-unused-expressions
        element.offsetHeight // trigger reflow
        // eslint-disable-next-line no-param-reassign
        element.style.display = originalDisplay || ''
      })
    },
    allowOnlyNumbers(event) {
      const { charCode, target } = event
      if (charCode < 48 || charCode > 57) {
        event.preventDefault()
      }

      // Also prevent input if already at 8 digits
      if (target.id === 'phone' && this.form.phone && this.form.phone.length >= 8) {
        event.preventDefault()
      }

      if (target.id === 'phone2' && this.form.phone2 && this.form.phone2.length >= 8) {
        event.preventDefault()
      }

      if (target.id === 'cin' && this.form.cin && this.form.cin.length >= 8) {
        event.preventDefault()
      }
    },

    formatPhone(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    formatCIN(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    async fetchRepositories() {
      this.repositoriesLoading = true
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data

        // Ensure styles are applied after data loads
        this.$nextTick(() => {
          this.refreshSelectStyles()
        })
      } catch (error) {
        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: 'Impossible de charger les dépôts',
        })
        console.error('Error fetching repositories:', error)
      } finally {
        this.repositoriesLoading = false
      }
    },

    onRepositoryChange(value) {
      // Handle repository selection change
      this.form.repository = value
      // Clear submitted state for this field if it's now valid
      if (value && this.submitted) {
        this.submitted = false
        this.$nextTick(() => {
          this.submitted = true
        })
      }
    },

    validateForm() {
      this.submitted = true

      // Basic validation
      if (!this.form.username) {
        this.showToast({
          variant: 'warning',
          title: 'Validation échouée',
          text: 'Le nom complet est requis',
        })
        return false
      }

      if (!this.form.email || !this.emailState) {
        this.showToast({
          variant: 'warning',
          title: 'Validation échouée',
          text: 'Une adresse email valide est requise',
        })
        return false
      }

      if (!this.form.password || !this.passwordState) {
        this.showToast({
          variant: 'warning',
          title: 'Validation échouée',
          text: 'Le mot de passe doit comporter au moins 8 caractères',
        })
        return false
      }

      if (this.form.password !== this.form.confirm_password) {
        this.showToast({
          variant: 'warning',
          title: 'Validation échouée',
          text: 'Les mots de passe ne correspondent pas',
        })
        return false
      }

      if (!this.form.repository) {
        this.showToast({
          variant: 'warning',
          title: 'Validation échouée',
          text: 'Veuillez sélectionner un dépôt',
        })
        return false
      }

      if (this.form.phone && !this.phoneState) {
        this.showToast({
          variant: 'warning',
          title: 'Validation échouée',
          text: 'Le numéro de téléphone doit contenir 8 chiffres',
        })
        return false
      }

      if (this.form.cin && !this.cinState) {
        this.showToast({
          variant: 'warning',
          title: 'Validation échouée',
          text: 'Le numéro de CIN doit contenir 8 chiffres',
        })
        return false
      }

      return true
    },

    async saveManager() {
      // Prevent double submission
      if (this.isSubmitting) return

      // Validate the form
      if (!this.validateForm()) return

      this.isSubmitting = true

      try {
        // Prepare payload
        const payload = {
          ...this.form,
          // Ensure we send valid data types
          repository: this.form.repository,
        }

        // Make API request to create manager
        await axios.post('/api/auth/register/manager/', payload)

        // Show success message
        this.showToast({
          variant: 'success',
          title: 'Succès',
          text: 'Manager créé avec succès',
        })

        // Redirect to managers list
        setTimeout(() => {
          this.$router.push({ name: 'manager-list' })
        }, 1000)
      } catch (error) {
        // Handle errors
        console.error('Error creating manager:', error)
        const errorMessage = error.response?.data?.message || 'Erreur lors de la création du manager'
        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: errorMessage,
        })
      } finally {
        this.isSubmitting = false
      }
    },

    showToast({ variant, title, text }) {
      let icon

      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else if (variant === 'warning') {
        icon = 'AlertTriangleIcon'
      } else {
        icon = 'AlertCircleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      }, {
        position: 'top-right',
        timeout: 5000,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.form-group {
  margin-bottom: 1.5rem;
}

.form-control:focus,
.custom-select:focus {
  border-color: #E31E24;
  box-shadow: 0 3px 10px 0 rgba(227, 30, 36, 0.1);
}

/* Enhanced select dropdown styling - global version for better reliability */
.enhanced-select {
  .vs__dropdown-toggle {
    padding: 6px 12px !important;
    border: 1px solid #d8d6de !important;
    border-radius: 0.357rem !important;
    background-color: #fff !important;
    transition: all 0.3s ease-in-out !important;
    min-height: 42px !important;

    &:hover {
      border-color: #b8b6c0 !important;
    }
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: #E31E24 !important;
    box-shadow: 0 3px 10px 0 rgba(227, 30, 36, 0.1) !important;
    background-color: #fff !important;
  }

  &.vs--focused .vs__dropdown-toggle {
    border-color: #E31E24 !important;
    box-shadow: 0 3px 10px 0 rgba(227, 30, 36, 0.1) !important;
  }

  .vs__selected {
    margin: 2px 2px !important;
    padding: 4px 8px !important;
    color: #5e5873 !important;
    background-color: rgba(227, 30, 36, 0.1) !important;
    border-radius: 4px !important;
    font-weight: 500 !important;
    display: flex !important;
    align-items: center !important;
  }

  .vs__selected-options {
    padding: 2px !important;
    flex-wrap: wrap !important;
  }

  .vs__actions {
    padding-right: 8px !important;
  }  .vs__clear {
    color: #b9b9c3 !important;

    &:hover {
      color: #E31E24 !important;
    }
  }

  .vs__open-indicator {
    color: #b9b9c3 !important;
    transition: transform 0.3s ease !important;

    &:hover {
      color: #E31E24 !important;
    }
  }

  &.vs--open .vs__open-indicator {
    transform: rotate(180deg) !important;
  }

  .vs__search {
    padding: 4px 0 !important;
    margin: 0 !important;

    &::placeholder {
      color: #b9b9c3 !important;
    }

    &:focus {
      outline: none !important;
    }
  }

  .vs__dropdown-menu {
    padding: 0 !important;
    border: 1px solid #d8d6de !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
    border-radius: 0.357rem !important;
    background: #fff !important;
    z-index: 1050 !important;
    max-height: 200px !important;
    overflow-y: auto !important;
  }

  .vs__dropdown-option {
    padding: 12px 20px !important;
    color: #5e5873 !important;
    transition: all 0.2s ease !important;
    cursor: pointer !important;
    border-bottom: 1px solid #f0f0f0 !important;

    &:last-child {
      border-bottom: none !important;
    }

    &:hover, &--highlight {
      background: linear-gradient(135deg, #f8f8f8 0%, #f0f0f0 100%) !important;
      color: #E31E24 !important;
    }

    &--selected {
      background: linear-gradient(135deg, #E31E24 0%, #B91C1C 100%) !important;
      color: #fff !important;
      font-weight: 600 !important;
    }

    &--disabled {
      background: #f5f5f5 !important;
      color: #b9b9c3 !important;
      cursor: not-allowed !important;
    }
  }

  .vs__no-options {
    padding: 20px !important;
    text-align: center !important;
    color: #b9b9c3 !important;
    font-style: italic !important;
  }

  .vs__spinner {
    border-color: #E31E24 transparent transparent !important;
  }

  /* Focus state enhancement */
  &:focus-within .vs__dropdown-toggle {
    border-color: #E31E24 !important;
    box-shadow: 0 3px 10px 0 rgba(227, 30, 36, 0.1) !important;
  }

  /* Error state styling */
  &.is-invalid .vs__dropdown-toggle {
    border-color: #ea5455 !important;

    &:focus, &:hover {
      border-color: #ea5455 !important;
      box-shadow: 0 3px 10px 0 rgba(234, 84, 85, 0.1) !important;
    }
  }

  /* Success state styling */
  &.is-valid .vs__dropdown-toggle {
    border-color: #28c76f !important;

    &:focus, &:hover {
      border-color: #28c76f !important;
      box-shadow: 0 3px 10px 0 rgba(40, 199, 111, 0.1) !important;
    }
  }
}

/* Ensure proper z-index for dropdowns */
.vs__dropdown-menu {
  z-index: 1050 !important;
}

/* Form enhancements */
.card {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1) !important;
  border: none !important;
  border-radius: 0.75rem !important;
}

.card-title {
  font-weight: 700 !important;
  color: #5e5873 !important;
}

h5 {
  color: #5e5873 !important;
  font-weight: 600 !important;
  margin-bottom: 1rem !important;

  .feather {
    color: #E31E24 !important;
  }
}

/* Form labels styling */
.form-group label {
  font-weight: 600 !important;
  color: #5e5873 !important;
  margin-bottom: 0.5rem !important;
}

/* Form controls enhanced styling */
.form-control {
  border: 1px solid #d8d6de !important;
  border-radius: 0.357rem !important;
  padding: 0.75rem 1rem !important;
  transition: all 0.3s ease !important;

  &:focus {
    border-color: #E31E24 !important;
    box-shadow: 0 3px 10px 0 rgba(227, 30, 36, 0.1) !important;
  }

  &:hover {
    border-color: #b8b6c0 !important;
  }
}

/* Button styling */
.btn-primary {
  background: linear-gradient(135deg, #E31E24 0%, #B91C1C 100%) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(227, 30, 36, 0.3) !important;
  transition: all 0.3s ease !important;

  &:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 20px rgba(227, 30, 36, 0.4) !important;
  }
}

.btn-outline-secondary {
  border-color: #d8d6de !important;
  color: #5e5873 !important;

  &:hover {
    background-color: #f8f8f8 !important;
    border-color: #b8b6c0 !important;
    color: #5e5873 !important;
  }
}

/* Force CSS re-application after navigation */
.page-enter-active, .page-leave-active {
  transition: opacity 0.3s;
}

.page-enter, .page-leave-to {
  opacity: 0;
}

/* Ensure v-select is always properly styled */
.enhanced-select * {
  box-sizing: border-box !important;
}

/* Fix for navigation CSS loading issues */
.router-view {
  transition: all 0.3s ease;
}

/* Loading state for repository select */
.enhanced-select.vs--loading .vs__open-indicator {
  animation: spin 1s linear infinite !important;
}

.enhanced-select .vs__spinner {
  .loading-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;

    div {
      width: 4px;
      height: 4px;
      margin: 0 2px;
      background-color: #E31E24;
      border-radius: 50%;
      animation: loading-bounce 1.4s ease-in-out infinite both;
    }

    .dot1 { animation-delay: -0.32s; }
    .dot2 { animation-delay: -0.16s; }
    .dot3 { animation-delay: 0s; }
  }
}

@keyframes loading-bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Enhancement for better visual feedback */
.form-group.has-error .enhanced-select .vs__dropdown-toggle {
  border-color: #ea5455 !important;
  animation: shake 0.5s ease-in-out !important;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

/* Ensure proper z-index stacking */
.enhanced-select .vs__dropdown-menu {
  z-index: 9999 !important;
  position: absolute !important;
}

/* Fix for modal/overlay issues */
.modal .enhanced-select .vs__dropdown-menu {
  z-index: 1060 !important;
}
</style>
